import { POWERUP_RADIUS } from "../../shared/constants.js";
import { powerupDef, type PowerupDTO } from "../../shared/protocol/index.js";
import { state } from "../state.js";
import type { Renderer } from "./Renderer.js";
import type { FogView } from "./types.js";
import { isPointVisible } from "./fog.js";
import { hexToRgba } from "./util.js";

export function drawPowerups(rd: Renderer, powerups: PowerupDTO[], nowMs: number, fog: FogView | null): void {
  const { ctx } = rd;
  const bob = Math.sin(nowMs / 300) * 1.5;
  for (const p of powerups) {
    if (fog && !isPointVisible(p.x, p.y, fog)) continue;
    const def = powerupDef(p.type);
    const s = POWERUP_RADIUS;
    ctx.save();

    if (state.battleCityEnabled) {
      // Battle City 8-bit retro powerup box (pulsing pixelated style)
      ctx.translate(p.x, p.y + (Math.floor(nowMs / 180) % 2) * 1.5);
      // Hard drop shadow, matching the tank/wall offset.
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      ctx.fillRect(-s + 3, -s + 3, s * 2, s * 2);
      const flash = Math.floor(nowMs / 120) % 2 === 0;
      ctx.fillStyle = flash ? "#f8b800" : "#d80000";
      ctx.fillRect(-s, -s, s * 2, s * 2);

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.strokeRect(-s + 1, -s + 1, s * 2 - 2, s * 2 - 2);

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 16px 'VT323', monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(def.emblem.substring(0, 1).toUpperCase(), 0, 0);
    } else if (state.modernEnabled) {
      // Modern 4K glowing holographic projection
      // Translate to the power-up position
      ctx.translate(p.x, p.y);

      // Ground shadow/ripple
      ctx.beginPath();
      ctx.arc(0, s * 1.2, s * 1.2, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0, 248, 248, 0.12)";
      ctx.fill();

      // Hologram base ring
      ctx.strokeStyle = "rgba(0, 248, 248, 0.4)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, s * 1.1, s * 0.9, 0, Math.PI * 2);
      ctx.stroke();

      // Projection beam: a faint vertical trapezoid from the base ring up to the floating emblem
      ctx.beginPath();
      ctx.moveTo(-s, bob * 2.0);
      ctx.lineTo(s, bob * 2.0);
      ctx.lineTo(s * 0.9, s * 1.1);
      ctx.lineTo(-s * 0.9, s * 1.1);
      ctx.closePath();
      ctx.fillStyle = "rgba(0, 248, 248, 0.05)";
      ctx.fill();

      // Now offset the floating hologram elements
      ctx.translate(0, bob * 2.0);

      // Glowing core
      const grad = ctx.createRadialGradient(0, 0, 1, 0, 0, s * 1.3);
      grad.addColorStop(0, "rgba(0, 248, 248, 0.5)");
      grad.addColorStop(0.5, "rgba(0, 248, 248, 0.2)");
      grad.addColorStop(1, "rgba(0, 248, 248, 0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, s * 1.3, 0, Math.PI * 2);
      ctx.fill();

      // Neon bounds ring (2-stroke fake instead of shadowBlur)
      // Pass 1: Wide low-alpha bloom ring
      ctx.strokeStyle = hexToRgba(def.color, 0.3);
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.arc(0, 0, s, 0, Math.PI * 2);
      ctx.stroke();

      // Pass 2: Core neon ring
      ctx.strokeStyle = def.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(0, 0, s, 0, Math.PI * 2);
      ctx.stroke();

      // Float emblem
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 13px 'Orbitron', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(def.emblem, 0, 0);
    } else if (state.realisticEnabled) {
      // Realistic military supply crate (olive drab metal box with stenciling)
      ctx.translate(p.x, p.y + bob * 0.5);
      // Cast shadow (sun top-left, matching tanks/walls).
      ctx.fillStyle = "rgba(22, 18, 8, 0.26)";
      ctx.fillRect(-s + 3, -s + 4, s * 2, s * 2);
      ctx.fillStyle = "#3e4830"; // Army olive drab
      ctx.fillRect(-s, -s, s * 2, s * 2);

      ctx.strokeStyle = "#252b1b"; // Dark metal borders
      ctx.lineWidth = 2;
      ctx.strokeRect(-s, -s, s * 2, s * 2);

      // Stencil warning stripes
      ctx.fillStyle = "#b8860b"; // Warning yellow-green
      ctx.fillRect(-s + 1, -s + 1, 4, 3);
      ctx.fillRect(s - 5, -s + 1, 4, 3);

      // Supply star emblem
      ctx.strokeStyle = "rgba(221, 232, 192, 0.85)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      // Simple star outline
      ctx.arc(0, 0, s * 0.45, 0, Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = "rgba(221, 232, 192, 0.85)";
      ctx.font = "bold 11px 'Courier New', monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(def.emblem, 0, 0.5);
    } else {
      // Default paper style crate
      ctx.translate(p.x, p.y + bob);
      ctx.fillStyle = "#c79a5b";
      ctx.fillRect(-s, -s, s * 2, s * 2);
      // Colored lid band in the power-up's color so crates read at a glance.
      ctx.fillStyle = hexToRgba(def.color, 0.4);
      ctx.fillRect(-s, -s, s * 2, 5);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#6e4a1e";
      ctx.strokeRect(-s, -s, s * 2, s * 2);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = "rgba(110,74,30,0.7)";
      ctx.beginPath();
      ctx.moveTo(-s, -s);
      ctx.lineTo(s, s);
      ctx.moveTo(s, -s);
      ctx.lineTo(-s, s);
      ctx.stroke();

      ctx.fillStyle = def.color;
      ctx.font = "bold 14px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.strokeStyle = "rgba(27,22,16,0.85)";
      ctx.lineWidth = 3;
      ctx.strokeText(def.emblem, 0, 1);
      ctx.fillText(def.emblem, 0, 1);
    }
    ctx.restore();
  }
  ctx.textBaseline = "alphabetic";
}
